import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

function ValutazionePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nomeAzienda: '',
    settore: '',
    persone: '',
    attivita: [] as string[],
    regia: '',
    chiarezza: '',
    decisioni: '',
    priorita: '',
    cambiamento: '',
    processo: '',
    feedback: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Valutazione di Compatibilità - Vertiix Digital",
    "description": "Questionario di valutazione per verificare la compatibilità strategica con Vertiix. Non è una richiesta di preventivo. Non tutte le aziende vengono accettate. Analisi oggettiva della maturità strategica e disponibilità al cambiamento.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/valutazione`
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      attivita: prev.attivita.includes(value)
        ? prev.attivita.filter(item => item !== value)
        : [...prev.attivita, value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nomeAzienda || !formData.priorita || !formData.cambiamento) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    if (formData.priorita.length > 500 || formData.cambiamento.length > 500) {
      alert('Le risposte aperte non possono superare i 500 caratteri');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nomeAzienda', formData.nomeAzienda);
      formBody.append('settore', formData.settore);
      formBody.append('persone', formData.persone);
      formBody.append('attivita', formData.attivita.join(', '));
      formBody.append('regia', formData.regia);
      formBody.append('chiarezza', formData.chiarezza);
      formBody.append('decisioni', formData.decisioni);
      formBody.append('priorita', formData.priorita);
      formBody.append('cambiamento', formData.cambiamento);
      formBody.append('processo', formData.processo);
      formBody.append('feedback', formData.feedback);

      const response = await fetch('https://readdy.ai/api/form/d5aebudcrgmf5papd91g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      const responseText = await response.text();
      
      if (response.ok || response.status === 200) {
        setSubmitStatus('success');
        setFormData({
          nomeAzienda: '',
          settore: '',
          persone: '',
          attivita: [],
          regia: '',
          chiarezza: '',
          decisioni: '',
          priorita: '',
          cambiamento: '',
          processo: '',
          feedback: ''
        });
        
        // Scroll to success message
        setTimeout(() => {
          const successMessage = document.querySelector('.bg-teal-50');
          if (successMessage) {
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        console.error('Response error:', response.status, responseText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Valutazione di Compatibilità - Vertiix Digital"
        description="Questionario di valutazione per verificare la compatibilità strategica con Vertiix. Non è una richiesta di preventivo. Non tutte le aziende vengono accettate. Analisi oggettiva della maturità strategica e disponibilità al cambiamento."
        keywords="valutazione vertiix, compatibilità strategica, questionario digital factory, maturità aziendale, processo valutazione"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/valutazione`}
        ogImage="https://static.readdy.ai/image/13db46b7263df02cb6f1b0f3fc2a6341/26134a7c97c7b6c8adda3aad56072f90.png"
        schema={schema}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/13db46b7263df02cb6f1b0f3fc2a6341/26134a7c97c7b6c8adda3aad56072f90.png" 
                alt="Vertiix" 
                className="h-16 md:h-24"
              />
            </Link>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium">Home</Link>
              <Link to="/problema" className="text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium">Il Problema</Link>
              <Link to="/vertiix" className="text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium">Cos'è Vertiix</Link>
              <Link to="/come-funziona" className="text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium">Come Funziona</Link>
              <Link to="/accesso" className="text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium">Come Si Accede</Link>
              <Link to="/valutazione" className="px-6 py-3 border border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6] hover:text-white transition-all whitespace-nowrap cursor-pointer">VALUTAZIONE</Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <Link to="/" className="block text-sm text-gray-700 hover:text-teal-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/problema" className="block text-sm text-gray-700 hover:text-teal-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Il Problema
              </Link>
              <Link to="/vertiix" className="block text-sm text-gray-700 hover:text-teal-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Cos'è Vertiix
              </Link>
              <Link to="/come-funziona" className="block text-sm text-gray-700 hover:text-teal-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Come Funziona
              </Link>
              <Link to="/accesso" className="block text-sm text-gray-700 hover:text-teal-600 transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Come Si Accede
              </Link>
              <Link to="/valutazione" className="block text-center px-6 py-3 border border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6] hover:text-white transition-all whitespace-nowrap cursor-pointer mt-4" onClick={() => setMobileMenuOpen(false)}>
                VALUTAZIONE
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 md:mb-8">Valutazione di Compatibilità</h1>
          <div className="w-16 md:w-20 h-1 bg-teal-600 mx-auto mb-6 md:mb-8"></div>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-4">
            Questo questionario serve a valutare se esistono le condizioni per lavorare con Vertiix.<br/>
            Non è una richiesta di preventivo.<br/>
            Non tutte le aziende che lo compilano vengono accettate.
          </p>
          <p className="text-sm md:text-base text-gray-500">
            La compilazione richiede circa 5–7 minuti.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-16 md:pb-24 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12" data-readdy-form id="valutazione-form">
            
            {/* Sezione 1 - Contesto Aziendale */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Contesto Aziendale</h2>
              
              <div className="space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="nomeAzienda" className="block text-sm font-medium text-gray-900 mb-2">
                    Nome dell'azienda <span className="text-teal-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="nomeAzienda"
                    name="nomeAzienda"
                    value={formData.nomeAzienda}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="settore" className="block text-sm font-medium text-gray-900 mb-2">
                    Settore di attività
                  </label>
                  <input
                    type="text"
                    id="settore"
                    name="settore"
                    value={formData.settore}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Quante persone sono oggi coinvolte nelle attività digitali o marketing?
                  </label>
                  <div className="space-y-2">
                    {['Solo il titolare', '2–3 persone', 'Team strutturato', 'Fornitori esterni multipli'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="persone"
                          value={option}
                          checked={formData.persone === option}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sezione 2 - Struttura Attuale */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Struttura Attuale</h2>
              
              <div className="space-y-5 md:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Quali attività digitali sono oggi attive?
                  </label>
                  <div className="space-y-2">
                    {['Sito web', 'Campagne pubblicitarie', 'SEO / contenuti', 'Social media', 'CRM / automazioni', 'Nessuna struttura definita'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.attivita.includes(option)}
                          onChange={() => handleCheckboxChange(option)}
                          className="w-4 h-4 text-teal-600 rounded focus:ring-teal-600"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Queste attività sono coordinate da una regia centrale?
                  </label>
                  <div className="space-y-2">
                    {['Sì', 'Parzialmente', 'No'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="regia"
                          value={option}
                          checked={formData.regia === option}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sezione 3 - Controllo e Decisione */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Controllo e Decisione</h2>
              
              <div className="space-y-5 md:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Quanto è chiaro oggi cosa sta realmente funzionando?
                  </label>
                  <div className="space-y-2">
                    {['Molto chiaro', 'Abbastanza chiaro', 'Poco chiaro', 'Non misurabile'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="chiarezza"
                          value={option}
                          checked={formData.chiarezza === option}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Le decisioni vengono prese principalmente su:
                  </label>
                  <div className="space-y-2">
                    {['Dati leggibili', 'Report frammentati', 'Sensazioni / esperienza', 'Urgenze del momento'].map((option) => (
                      <label key={option} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="decisioni"
                          value={option}
                          checked={formData.decisioni === option}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sezione 4 - Maturità Strategica */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Maturità Strategica</h2>
              
              <div className="space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="priorita" className="block text-sm font-medium text-gray-900 mb-2">
                    Qual è oggi la tua priorità reale? <span className="text-teal-600">*</span>
                  </label>
                  <textarea
                    id="priorita"
                    name="priorita"
                    value={formData.priorita}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Es. stabilizzare la crescita, migliorare il controllo, ridurre dispersione, scalare in modo sostenibile…"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.priorita.length}/500 caratteri</p>
                </div>

                <div>
                  <label htmlFor="cambiamento" className="block text-sm font-medium text-gray-900 mb-2">
                    Se il sistema fosse sotto controllo, cosa cambierebbe concretamente? <span className="text-teal-600">*</span>
                  </label>
                  <textarea
                    id="cambiamento"
                    name="cambiamento"
                    value={formData.cambiamento}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Es. decisioni più rapide, meno sprechi, maggiore prevedibilità, meno dipendenza da singole persone…"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.cambiamento.length}/500 caratteri</p>
                </div>
              </div>
            </div>

            {/* Sezione 5 - Disponibilità al Processo */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Disponibilità al Processo</h2>
              
              <div>
                <p className="text-sm text-gray-700 mb-4">
                  Vertiix lavora tramite un processo strutturato e continuativo, non su interventi spot.<br/>
                  Seleziona l'opzione che descrive meglio la tua posizione.
                </p>
                <div className="space-y-2">
                  {['È esattamente ciò che cerco', 'Potrebbe avere senso', 'Preferisco interventi rapidi'].map((option) => (
                    <label key={option} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="processo"
                        value={option}
                        checked={formData.processo === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Sezione 6 - Filtro Finale */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Filtro Finale</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Se dalla valutazione emergesse che non ci sono le condizioni per lavorare insieme, come reagiresti?
                </label>
                <div className="space-y-2">
                  {['Accetterei il feedback', 'Vorrei comunque una proposta', 'Non lo so'].map((option) => (
                    <label key={option} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="feedback"
                        value={option}
                        checked={formData.feedback === option}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-600"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="text-center space-y-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Invio in corso...' : 'Richiedi la valutazione'}
              </button>

              <p className="text-sm text-gray-500">
                La valutazione non è una call di vendita.<br/>
                Nessun impegno immediato. Nessuna pressione commerciale.
              </p>

              {submitStatus === 'success' && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    La tua richiesta verrà analizzata.<br/>
                    Se emergerà un allineamento reale, verrai contattato per la fase di valutazione.<br/>
                    In caso contrario, riceverai un feedback sintetico.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <p className="text-sm text-red-700">
                    Si è verificato un errore. Riprova più tardi.
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-12">
            <div className="md:col-span-4">
              <img 
                src="https://static.readdy.ai/image/13db46b7263df02cb6f1b0f3fc2a6341/26134a7c97c7b6c8adda3aad56072f90.png" 
                alt="Vertiix" 
                className="h-48 mb-8"
                style={{ filter: 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 30px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 45px rgba(255, 255, 255, 0.3))' }}
              />
              <p className="text-sm text-gray-400 leading-relaxed">Digital Factory per aziende che vogliono governare la crescita, non subirla.</p>
            </div>
            
            <div className="md:col-span-2">
              <h4 className="text-sm font-semibold text-white mb-6">Navigazione</h4>
              <nav className="space-y-3">
                <Link to="/" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">Home</Link>
                <Link to="/problema" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">Il Problema</Link>
                <Link to="/vertiix" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">Cos'è Vertiix</Link>
                <Link to="/come-funziona" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">Come Funziona</Link>
                <Link to="/accesso" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">Come Si Accede</Link>
              </nav>
            </div>
            
            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold text-white mb-6">Contatti</h4>
              <p className="text-sm text-gray-400">info@vertiix.ch</p>
            </div>
            
            <div className="md:col-span-3">
              <h4 className="text-sm font-semibold text-white mb-6">Azione</h4>
              <Link to="/valutazione" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors mb-3">Valutazione</Link>
              <Link to="/about-vertiix" className="block text-sm text-gray-400 hover:text-[#14B8A6] transition-colors">About Vertiix</Link>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              © 2025 Vertiix. Sistema operativo della crescita.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <a 
                href="https://ti.chregister.ch/cr-portal/auszug/auszug.xhtml?uid=CHE-410.417.125" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-xs text-gray-500 hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                CHE-410.417.125
              </a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-[#14B8A6] transition-colors">
                Powered by vertiix
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ValutazionePage;
